/**
 * Use the `pure` entry so importing this module does not run Stripe's eager
 * script loader (see dist/index.js: microtask → document.querySelectorAll).
 * That side effect throws `document is not defined` during Next.js SSR.
 * @see https://github.com/stripe/stripe-js#importing-loadstripe-without-side-effects
 */
import { loadStripe } from "@stripe/stripe-js/pure";
import { getStripePriceId } from "../util/prices";
import { apiRequestExternal } from "./apiRequestExternal";
import { apiValidateArguments } from "./apiValidateArguments";
import { logger } from "../util/logger";

let stripePromise;

/**
 * Stripe.js must not load during SSR / RSC: loadStripe() touches `document`.
 * Call this only from browser-only code paths (e.g. checkout redirects).
 */
function getStripeJs() {
  if (typeof window === "undefined") {
    return Promise.reject(
      new Error("Stripe.js is only available in the browser")
    );
  }
  if (!stripePromise) {
    const key = process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY;
    if (!key) {
      return Promise.reject(
        new Error("Missing NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY")
      );
    }
    stripePromise = loadStripe(key);
  }
  return stripePromise;
}

// Create Stripe checkout session ("Add Stripe User Subscription")
export async function redirectToCheckoutPurchase({
  accessToken,
  purchaseId,
  purchaseType,
  successUrl,
  cancelUrl,
}) {
  try {
    // Validate args
    apiValidateArguments({ accessToken, purchaseId, successUrl, cancelUrl });

    // Return custom response data
    const response = await apiRequestExternal(
      `${process.env.NEXT_PUBLIC_COMPOSER_URL}/v1/public/regional/auth/user/users/stripe/purchase`,
      "POST",
      {
        purchase_type: purchaseType,
        purchase_id: purchaseId,
        success_url: successUrl,
        cancel_url: cancelUrl,
      },
      { Authorization: accessToken }
    );

    // Get Stripe session ID from server response
    const data = response.data;
    const sessionId = data.session_id;
    logger("composer res:", response.data);

    if (!data || !sessionId) {
      throw new Error("Received invalid response from server");
    }

    const stripe = await getStripeJs();
    return stripe.redirectToCheckout({
      sessionId,
    });
  } catch (error) {
    console.error("Error:", error);
    throw error;
  }
}

// Create Stripe checkout session ("Add Stripe User Subscription")
export async function redirectToCheckoutSubscribe({
  accessToken,
  planId,
  successUrl,
  cancelUrl,
}) {
  try {
    // Validate args
    apiValidateArguments({ accessToken, planId, successUrl, cancelUrl });

    // Get Stripe price ID for selected plan
    const stripePriceId = getStripePriceId(planId);

    if (!stripePriceId) {
      throw new Error("Unable to fetch price ID.");
    }

    // Return custom response data
    const response = await apiRequestExternal(
      `${process.env.NEXT_PUBLIC_COMPOSER_URL}/v1/public/regional/auth/user/users/stripe/purchase`,
      "POST",
      {
        subscription_id: stripePriceId,
        success_url: successUrl,
        // success_url:`${window.location.origin}?paid=true`,
        // cancel_url: `${window.location.origin}/pricing`,
        cancel_url: cancelUrl,
      },
      { Authorization: accessToken }
    );

    // Update the URL shown in the browser address bar to /pricing without adding a new entry to the browser history.
    // So when users click the browser back button, they will go to the page they were on BEFORE /pricing.
    if (!stripePriceId) window.history.replaceState({}, "", "/pricing");

    // Get Stripe session ID from server response
    const data = response.data;
    const sessionId = data.id;
    logger("composer res:", response.data);

    if (!data || !data.id) {
      throw new Error("Received invalid response from server");
    }

    const stripe = await getStripeJs();
    return stripe.redirectToCheckout({
      sessionId,
    });
  } catch (error) {
    console.error("Error:", error);
    throw error;
  }
}

/**
 * Opens Stripe Customer Portal (change plan, downgrade to Community, payment method).
 * Requires an active Stripe customer on the user record.
 */
export async function openStripeBillingPortal({ accessToken, returnUrl }) {
  try {
    apiValidateArguments({ accessToken, returnUrl });

    const response = await apiRequestExternal(
      `${process.env.NEXT_PUBLIC_COMPOSER_URL}/v1/public/regional/auth/user/users/stripe/billing`,
      "POST",
      { return_url: returnUrl },
      { Authorization: accessToken }
    );

    const url = response?.data?.url;
    if (!url) {
      throw new Error("No billing portal URL in response");
    }
    window.location.href = url;
  } catch (error) {
    console.error("openStripeBillingPortal:", error);
    throw error;
  }
}

/** Hosted Checkout for self-serve Pro subscription; returns Stripe Checkout URL. */
export async function createProSubscriptionCheckoutUrl({
  accessToken,
  successUrl,
  cancelUrl,
  billingInterval,
}) {
  try {
    apiValidateArguments({ accessToken, successUrl, cancelUrl });
    const bi = billingInterval === "year" ? "year" : "month";
    const response = await apiRequestExternal(
      `${process.env.NEXT_PUBLIC_COMPOSER_URL}/v1/public/regional/auth/user/users/stripe/checkout/pro-subscription`,
      "POST",
      {
        success_url: successUrl,
        cancel_url: cancelUrl,
        billing_interval: bi,
      },
      { Authorization: accessToken }
    );
    const url = response?.data?.url;
    if (!url || typeof url !== "string") {
      throw new Error("No checkout URL in Composer response");
    }
    return url;
  } catch (error) {
    console.error("createProSubscriptionCheckoutUrl:", error);
    throw error;
  }
}

/** Hosted Checkout for self-serve Team subscription; returns Stripe Checkout URL. */
export async function createTeamSubscriptionCheckoutUrl({
  accessToken,
  successUrl,
  cancelUrl,
  billingInterval,
  seatCount,
}) {
  try {
    apiValidateArguments({ accessToken, successUrl, cancelUrl });
    const bi = billingInterval === "year" ? "year" : "month";
    const seats = Number(seatCount);
    const response = await apiRequestExternal(
      `${process.env.NEXT_PUBLIC_COMPOSER_URL}/v1/public/regional/auth/user/users/stripe/checkout/team-subscription`,
      "POST",
      {
        success_url: successUrl,
        cancel_url: cancelUrl,
        billing_interval: bi,
        seat_count: seats,
      },
      { Authorization: accessToken }
    );
    const url = response?.data?.url;
    if (!url || typeof url !== "string") {
      throw new Error("No checkout URL in Composer response");
    }
    return url;
  } catch (error) {
    console.error("createTeamSubscriptionCheckoutUrl:", error);
    throw error;
  }
}

/** GET Composer subscription summary (period end, cancel-at-period-end). */
export async function fetchStripeSubscriptionStatus(accessToken) {
  try {
    apiValidateArguments({ accessToken });
    const response = await apiRequestExternal(
      `${process.env.NEXT_PUBLIC_COMPOSER_URL}/v1/public/regional/auth/user/users/stripe/subscription`,
      "GET",
      null,
      { Authorization: accessToken }
    );
    return response.data;
  } catch (error) {
    console.error("fetchStripeSubscriptionStatus:", error);
    throw error;
  }
}

/** Cancel Pro auto-renewal at end of current term (no partial refund). */
export async function cancelProSubscriptionRenewal(accessToken) {
  try {
    apiValidateArguments({ accessToken });
    const response = await apiRequestExternal(
      `${process.env.NEXT_PUBLIC_COMPOSER_URL}/v1/public/regional/auth/user/users/stripe/subscription/cancel-renewal`,
      "POST",
      {},
      { Authorization: accessToken }
    );
    return response.data;
  } catch (error) {
    console.error("cancelProSubscriptionRenewal:", error);
    throw error;
  }
}

/** Re-enable auto-renewal when the subscription was set to cancel at period end. */
export async function resumeProSubscriptionRenewal(accessToken) {
  try {
    apiValidateArguments({ accessToken });
    const response = await apiRequestExternal(
      `${process.env.NEXT_PUBLIC_COMPOSER_URL}/v1/public/regional/auth/user/users/stripe/subscription/resume-renewal`,
      "POST",
      {},
      { Authorization: accessToken }
    );
    return response.data;
  } catch (error) {
    console.error("resumeProSubscriptionRenewal:", error);
    throw error;
  }
}

/** Proration preview for replacing Team line item with Pro (same Stripe subscription). */
export async function previewSwitchTeamToPro(accessToken, billingInterval) {
  try {
    apiValidateArguments({ accessToken });
    const bi = billingInterval === "year" ? "year" : "month";
    const response = await apiRequestExternal(
      `${process.env.NEXT_PUBLIC_COMPOSER_URL}/v1/public/regional/auth/user/users/stripe/subscription/preview-switch-team-to-pro`,
      "POST",
      { billing_interval: bi },
      { Authorization: accessToken }
    );
    return response.data;
  } catch (error) {
    console.error("previewSwitchTeamToPro:", error);
    throw error;
  }
}

/** Apply Team → Pro swap on existing subscription (no second subscription). */
export async function switchTeamToProSubscription(accessToken, billingInterval) {
  try {
    apiValidateArguments({ accessToken });
    const bi = billingInterval === "year" ? "year" : "month";
    const response = await apiRequestExternal(
      `${process.env.NEXT_PUBLIC_COMPOSER_URL}/v1/public/regional/auth/user/users/stripe/subscription/switch-team-to-pro`,
      "POST",
      { billing_interval: bi },
      { Authorization: accessToken }
    );
    return response.data;
  } catch (error) {
    console.error("switchTeamToProSubscription:", error);
    throw error;
  }
}

/** Proration preview for replacing Pro line item with Team (same Stripe subscription). */
export async function previewSwitchProToTeam(accessToken, billingInterval, seatCount) {
  try {
    apiValidateArguments({ accessToken });
    const bi = billingInterval === "year" ? "year" : "month";
    const seats = Number(seatCount);
    const response = await apiRequestExternal(
      `${process.env.NEXT_PUBLIC_COMPOSER_URL}/v1/public/regional/auth/user/users/stripe/subscription/preview-switch-pro-to-team`,
      "POST",
      { billing_interval: bi, seat_count: seats },
      { Authorization: accessToken }
    );
    return response.data;
  } catch (error) {
    console.error("previewSwitchProToTeam:", error);
    throw error;
  }
}

/** Apply Pro → Team swap on existing subscription (no second subscription). */
export async function switchProToTeamSubscription(accessToken, billingInterval, seatCount) {
  try {
    apiValidateArguments({ accessToken });
    const bi = billingInterval === "year" ? "year" : "month";
    const seats = Number(seatCount);
    const response = await apiRequestExternal(
      `${process.env.NEXT_PUBLIC_COMPOSER_URL}/v1/public/regional/auth/user/users/stripe/subscription/switch-pro-to-team`,
      "POST",
      { billing_interval: bi, seat_count: seats },
      { Authorization: accessToken }
    );
    return response.data;
  } catch (error) {
    console.error("switchProToTeamSubscription:", error);
    throw error;
  }
}

const teamsBase = () =>
  `${process.env.NEXT_PUBLIC_COMPOSER_URL}/v1/public/regional/auth/user/teams`;

/** GET …/teams/me — team_id and team_display_name for any team member. */
export async function fetchTeamMe(accessToken) {
  try {
    apiValidateArguments({ accessToken });
    const response = await apiRequestExternal(
      `${teamsBase()}/me`,
      "GET",
      null,
      { Authorization: accessToken },
      false,
      false
    );
    return response?.data ?? response;
  } catch (error) {
    console.error("fetchTeamMe:", error);
    throw error;
  }
}

/** GET …/teams/admin — team owner only. */
export async function fetchTeamAdmin(accessToken) {
  try {
    apiValidateArguments({ accessToken });
    const response = await apiRequestExternal(
      `${teamsBase()}/admin`,
      "GET",
      null,
      { Authorization: accessToken }
    );
    return response.data;
  } catch (error) {
    console.error("fetchTeamAdmin:", error);
    throw error;
  }
}

/** POST …/teams/invite — viewer-only invites take no seat. */
export async function teamInviteMember(accessToken, email, viewerOnly) {
  try {
    apiValidateArguments({ accessToken, email });
    const response = await apiRequestExternal(
      `${teamsBase()}/invite`,
      "POST",
      { email, viewer_only: viewerOnly === true },
      { Authorization: accessToken }
    );
    return response.data;
  } catch (error) {
    console.error("teamInviteMember:", error);
    throw error;
  }
}

/** POST …/teams/invites/:inviteId/remind */
export async function teamRemindInvite(accessToken, inviteId) {
  try {
    apiValidateArguments({ accessToken, inviteId });
    const response = await apiRequestExternal(
      `${teamsBase()}/invites/${encodeURIComponent(inviteId)}/remind`,
      "POST",
      null,
      { Authorization: accessToken }
    );
    return response.data;
  } catch (error) {
    console.error("teamRemindInvite:", error);
    throw error;
  }
}

/** DELETE …/teams/invites/:inviteId */
export async function teamRevokeInvite(accessToken, inviteId) {
  try {
    apiValidateArguments({ accessToken, inviteId });
    const response = await apiRequestExternal(
      `${teamsBase()}/invites/${encodeURIComponent(inviteId)}`,
      "DELETE",
      null,
      { Authorization: accessToken }
    );
    return response.data;
  } catch (error) {
    console.error("teamRevokeInvite:", error);
    throw error;
  }
}

/** GET …/teams/invites/mine — pending invites addressed to the caller's account email. */
export async function fetchMyTeamInvites(accessToken) {
  try {
    apiValidateArguments({ accessToken });
    const response = await apiRequestExternal(
      `${teamsBase()}/invites/mine`,
      "GET",
      null,
      { Authorization: accessToken },
      false,
      false
    );
    return response?.data ?? response;
  } catch (error) {
    console.error("fetchMyTeamInvites:", error);
    throw error;
  }
}

/** POST …/teams/invites/:inviteId/:action — invitee accepts, declines, or dismisses the notice. */
async function teamRespondToInvite(accessToken, inviteId, action) {
  apiValidateArguments({ accessToken, inviteId });
  const response = await apiRequestExternal(
    `${teamsBase()}/invites/${encodeURIComponent(inviteId)}/${action}`,
    "POST",
    null,
    { Authorization: accessToken }
  );
  return response.data;
}

export async function teamAcceptInvite(accessToken, inviteId) {
  try {
    return await teamRespondToInvite(accessToken, inviteId, "accept");
  } catch (error) {
    console.error("teamAcceptInvite:", error);
    throw error;
  }
}

export async function teamDeclineInvite(accessToken, inviteId) {
  try {
    return await teamRespondToInvite(accessToken, inviteId, "decline");
  } catch (error) {
    console.error("teamDeclineInvite:", error);
    throw error;
  }
}

/** Hide the home notice for one invite; it stays answerable on Account. */
export async function teamDismissInviteNotice(accessToken, inviteId) {
  try {
    return await teamRespondToInvite(accessToken, inviteId, "dismiss-notice");
  } catch (error) {
    console.error("teamDismissInviteNotice:", error);
    throw error;
  }
}

/** GET …/teams/organizer-invites/mine — pending organizer invites for the caller's verified email. */
export async function fetchMyOrganizerInvites(accessToken) {
  try {
    apiValidateArguments({ accessToken });
    const response = await apiRequestExternal(
      `${teamsBase()}/organizer-invites/mine`,
      "GET",
      null,
      { Authorization: accessToken },
      false,
      false
    );
    return response?.data ?? response;
  } catch (error) {
    console.error("fetchMyOrganizerInvites:", error);
    throw error;
  }
}

/** POST …/teams/organizer-invites/:inviteId/:action — accept or decline an organizer invite. */
async function organizerInviteRespond(accessToken, inviteId, action) {
  apiValidateArguments({ accessToken, inviteId });
  const response = await apiRequestExternal(
    `${teamsBase()}/organizer-invites/${encodeURIComponent(inviteId)}/${action}`,
    "POST",
    null,
    { Authorization: accessToken }
  );
  return response.data;
}

/** Accepting creates the team and starts the trial; resolves `{ok, team_id, expires_at}`. */
export async function organizerAcceptInvite(accessToken, inviteId) {
  try {
    return await organizerInviteRespond(accessToken, inviteId, "accept");
  } catch (error) {
    console.error("organizerAcceptInvite:", error);
    throw error;
  }
}

export async function organizerDeclineInvite(accessToken, inviteId) {
  try {
    return await organizerInviteRespond(accessToken, inviteId, "decline");
  } catch (error) {
    console.error("organizerDeclineInvite:", error);
    throw error;
  }
}

/** POST …/teams/organizer-onboarding/dismiss — owner hides the onboarding checklist. */
export async function organizerDismissOnboarding(accessToken) {
  try {
    apiValidateArguments({ accessToken });
    const response = await apiRequestExternal(
      `${teamsBase()}/organizer-onboarding/dismiss`,
      "POST",
      null,
      { Authorization: accessToken }
    );
    return response.data;
  } catch (error) {
    console.error("organizerDismissOnboarding:", error);
    throw error;
  }
}

/** POST …/teams/leave — members only; the owner is refused. */
export async function teamLeave(accessToken) {
  try {
    apiValidateArguments({ accessToken });
    const response = await apiRequestExternal(
      `${teamsBase()}/leave`,
      "POST",
      null,
      { Authorization: accessToken }
    );
    return response.data;
  } catch (error) {
    console.error("teamLeave:", error);
    throw error;
  }
}

/** DELETE …/teams/members/:memberNuid */
export async function teamRemoveMember(accessToken, memberNuid) {
  try {
    apiValidateArguments({ accessToken, memberNuid });
    const response = await apiRequestExternal(
      `${teamsBase()}/members/${encodeURIComponent(memberNuid)}`,
      "DELETE",
      null,
      { Authorization: accessToken }
    );
    return response.data;
  } catch (error) {
    console.error("teamRemoveMember:", error);
    throw error;
  }
}

/** POST …/teams/owner/consumes-seat */
export async function teamSetOwnerConsumesSeat(accessToken, consumesSeat) {
  try {
    apiValidateArguments({ accessToken });
    const response = await apiRequestExternal(
      `${teamsBase()}/owner/consumes-seat`,
      "POST",
      { consumes_seat: Boolean(consumesSeat) },
      { Authorization: accessToken }
    );
    return response.data;
  } catch (error) {
    console.error("teamSetOwnerConsumesSeat:", error);
    throw error;
  }
}

/** PATCH …/teams/display-name — null clears the custom team label. */
export async function teamSetTeamDisplayName(accessToken, teamDisplayName) {
  try {
    apiValidateArguments({ accessToken });
    const response = await apiRequestExternal(
      `${teamsBase()}/display-name`,
      "PATCH",
      { team_display_name: teamDisplayName == null ? null : String(teamDisplayName) },
      { Authorization: accessToken }
    );
    return response.data;
  } catch (error) {
    console.error("teamSetTeamDisplayName:", error);
    throw error;
  }
}

// Create Stripe billing portal session ("Update Stripe Billing")
// export async function redirectToBilling(accessToken) {
//   try {
//     if (!accessToken) throw new Error("Unable to retrieve access token");

//     // Return custom response data
//     const response = await apiRequestExternal(
//       `${process.env.NEXT_PUBLIC_COMPOSER_URL}/v1/auth/user/users/stripe/billing`,
//       "POST",
//       {
//         // return_url: `${window.location.origin}/settings/account`,
//         return_url: `${window.location.origin}/settings/account`,
//       },
//       { Authorization: accessToken }
//     );

//     // Get Stripe session ID from server response
//     const session = response.data;
//     logger("composer res:", response.data);

//     if (!session || !session.url)
//       throw new Error("Received invalid response from server");

//     // If user clicks back, make sure they go to the page they were on BEFORE /pricing
//     window.history.replaceState({}, "", "/settings/account");

//     // window.location.href = session.url;
//   } catch (error) {
//     console.error("Error:", error);
//     throw error;
//   }
// }

// export async function redirectToBilling(token) {
//   // Create a billing session
//   const session = await apiRequest("v1/auth/user/users/stripe/billing", "POST", {
//     token_id: "",
//     return_url: `${window.location.origin}/settings/account`,
//   });

//   // Ensure if user clicks browser back button from billing they go to /settings/account
//   // instead of this page or they'll redirect right back to billing.
//   window.history.replaceState({}, "", "/settings/account");

//   // Redirect to billing session url
//   window.location.href = session.url;
// }
